"use client";

import type { ReactNode } from "react";
import type { IconType } from "@astryxdesign/core/Icon";
import { Icon } from "@astryxdesign/core/Icon";
import { VStack } from "@astryxdesign/core/VStack";
import { LandingCanvasText } from "@/components/landing/landing-canvas-text";
import { resolveLandingNavHref } from "@/lib/landing/nav-href";
import type {
  LandingFooterColumn,
  LandingFooterColumnItem,
} from "@/lib/landing/puck/footer-defaults";
import { parseLandingIcon } from "@/lib/landing/puck/icon-options";
import { researchIcons } from "@/lib/icons/research-icons";
import { landingFont } from "@/lib/landing/brandbook-ui";
import {
  landingTextProps,
  styledTextString,
  type LandingTextValue,
} from "@/lib/landing/puck/styled-text";

const footerTextClass = `${landingFont} break-words text-[length:var(--landing-nav-font-size,14px)] font-normal leading-relaxed text-[var(--landing-nav-color,var(--color-text-primary))]`;
const footerMutedTextClass = `${footerTextClass} text-[var(--landing-nav-color-secondary,var(--color-text-secondary))]`;
const footerLinkClass = `${landingFont} break-words text-[length:var(--landing-nav-font-size,14px)] font-normal leading-relaxed text-[var(--landing-nav-color,var(--color-text-primary))] underline decoration-transparent underline-offset-[3px] transition-colors hover:font-medium hover:text-[var(--landing-nav-color-active,#D6002E)] hover:decoration-[var(--landing-nav-color-active,#D6002E)]`;

function footerIcon(iconName: unknown): IconType {
  const key = parseLandingIcon(iconName);
  return researchIcons[key];
}

function FooterItemIcon({ icon, link }: { icon: IconType; link?: boolean }) {
  return (
    <span
      className={
        link
          ? "mt-0.5 shrink-0 transition-colors group-hover:text-[var(--landing-nav-color-active,#D6002E)]"
          : "mt-0.5 shrink-0"
      }
    >
      <Icon icon={icon} size="sm" color="secondary" />
    </span>
  );
}

function FooterTextItem({
  item,
  field,
}: {
  item: LandingFooterColumnItem;
  field: string;
}) {
  return (
    <div className="flex min-w-0 items-start gap-2.5">
      <FooterItemIcon icon={footerIcon(item.icon)} />
      <LandingCanvasText
        field={`${field}.label`}
        value={item.label}
        label="Текст"
        multiline
      >
        <span className={item.muted ? footerMutedTextClass : footerTextClass}>
          <span {...landingTextProps(item.label)} />
        </span>
      </LandingCanvasText>
    </div>
  );
}

function FooterLinkItem({
  item,
  field,
}: {
  item: LandingFooterColumnItem;
  field: string;
}) {
  const href = resolveLandingNavHref(item.href || "#");
  return (
    <a
      href={href}
      className={`group flex min-w-0 items-start gap-2.5 ${footerLinkClass}`}
      {...(item.external ? { target: "_blank", rel: "noreferrer" } : {})}
    >
      <FooterItemIcon icon={footerIcon(item.icon)} link />
      <LandingCanvasText field={`${field}.label`} value={item.label} label="Подпись ссылки">
        <span {...landingTextProps(item.label)} />
      </LandingCanvasText>
    </a>
  );
}

function FooterColumnItems({
  items,
  columnIndex,
}: {
  items: LandingFooterColumnItem[];
  columnIndex: number;
}) {
  return (
    <VStack gap={3} align="start" className="min-w-0">
      {items.map((item, itemIndex) => {
        const field = `columns.${columnIndex}.items.${itemIndex}`;
        if (item.kind === "text") {
          return <FooterTextItem key={field} item={item} field={field} />;
        }
        return <FooterLinkItem key={field} item={item} field={field} />;
      })}
    </VStack>
  );
}

type Props = {
  columns: LandingFooterColumn[];
  columnsSlot?: ReactNode;
};

export function LandingFooterColumnsView({ columns, columnsSlot }: Props) {
  if (columnsSlot) {
    return <>{columnsSlot}</>;
  }

  return (
    <div className="grid w-full grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 lg:items-start">
      {columns.map((column, columnIndex) => (
        <VStack key={column.id} gap={3} align="start" className="min-w-0 w-full">
          <LandingCanvasText
            field={`columns.${columnIndex}.title`}
            value={column.title}
            label="Заголовок колонки"
          >
            <p
              className={`${landingFont} uppercase tracking-wide text-[length:var(--landing-nav-font-size,14px)] font-normal text-[var(--landing-nav-color,var(--color-text-primary))]`}
            >
              {styledTextString(column.title) || "Колонка"}
            </p>
          </LandingCanvasText>
          <FooterColumnItems items={column.items} columnIndex={columnIndex} />
        </VStack>
      ))}
    </div>
  );
}
